import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Package, Split, Truck, Scale, Calendar, RefreshCw } from "lucide-react";
import { BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DashboardPage = ({ user, token }) => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hubs, setHubs] = useState([]);
  const [selectedHub, setSelectedHub] = useState(user?.hub_id || "all");
  const [selectedDate, setSelectedDate] = useState("");
  const [dateFilter, setDateFilter] = useState("all"); // "all", "today", "custom"

  useEffect(() => {
    fetchHubs();
  }, []);

  useEffect(() => {
    fetchDashboardStats();
  }, [selectedHub, selectedDate, dateFilter]);

  const fetchHubs = async () => {
    try {
      const response = await axios.get(`${API}/hubs`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHubs(response.data);
    } catch (error) {
      console.error("Error fetching hubs:", error);
    }
  };

  const fetchDashboardStats = async () => {
    setLoading(true);
    try {
      const params = {};
      // For staff/managers, always use their assigned hub
      if (user?.hub_id) {
        params.hub_id = user.hub_id;
      } else if (selectedHub && selectedHub !== "all") {
        params.hub_id = selectedHub;
      }
      
      // Date filter
      if (dateFilter === "today") {
        params.date = new Date().toISOString().split('T')[0];
      } else if (dateFilter === "custom" && selectedDate) {
        params.date = selectedDate;
      }
      
      const response = await axios.get(`${API}/reports/dashboard`, {
        headers: { Authorization: `Bearer ${token}` },
        params
      });
      setStats(response.data);
    } catch (error) {
      console.error("Error fetching dashboard stats:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDateFilterChange = (value) => {
    setDateFilter(value);
    if (value === "today") {
      setSelectedDate(new Date().toISOString().split('T')[0]);
    } else if (value === "all") {
      setSelectedDate("");
    }
  };

  const statsCards = [
    { title: "Total Inward", value: `${stats?.total_inward?.toFixed(1) || 0} kg`, count: stats?.inward_count || 0, icon: Package, color: "text-green-600", bg: "bg-green-50" },
    { title: "Segregation", value: `${stats?.total_segregation?.toFixed(1) || 0} kg`, count: stats?.segregation_count || 0, icon: Split, color: "text-blue-600", bg: "bg-blue-50" },
    { title: "Dispatched", value: `${stats?.total_dispatch?.toFixed(1) || 0} kg`, count: stats?.dispatch_count || 0, icon: Truck, color: "text-orange-600", bg: "bg-orange-50" },
    { title: "Balance Stock", value: `${stats?.balance_stock?.toFixed(1) || 0} kg`, count: "-", icon: Scale, color: "text-purple-600", bg: "bg-purple-50" },
  ];

  const chartData = [
    { name: "Inward", value: stats?.total_inward || 0, fill: "#22c55e" },
    { name: "Segregation", value: stats?.total_segregation || 0, fill: "#3b82f6" },
    { name: "Dispatch", value: stats?.total_dispatch || 0, fill: "#f97316" },
  ];

  const getHubName = () => {
    if (user?.hub_id) {
      const hub = hubs.find(h => h.id === user.hub_id);
      return hub?.name || "Your Hub";
    }
    if (selectedHub === "all") return "All Hubs";
    const hub = hubs.find(h => h.id === selectedHub);
    return hub?.name || "Selected Hub";
  };

  const getDateLabel = () => {
    if (dateFilter === "all") return "All Time";
    if (dateFilter === "today") return "Today";
    if (dateFilter === "custom" && selectedDate) {
      return new Date(selectedDate).toLocaleDateString('en-IN', { 
        day: 'numeric', 
        month: 'short', 
        year: 'numeric' 
      });
    }
    return "";
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6 min-h-full relative" data-testid="dashboard-page">
      <div className="absolute top-0 right-0 w-48 sm:w-96 h-48 sm:h-96 bg-gradient-to-br from-green-100/50 to-blue-100/50 rounded-full filter blur-3xl -z-10"></div>
      
      <div className="flex flex-col gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold font-heading tracking-tight text-gray-800">Dashboard</h1>
          <p className="text-muted-foreground text-sm sm:text-base mt-1">
            <span className="font-semibold text-primary">{getHubName()}</span>
            {" • "}
            <span className="font-medium">{getDateLabel()}</span>
          </p>
        </div>
        
        {/* Filters Row */}
        <div className="flex flex-col sm:flex-row gap-3 sm:items-end">
          {/* Hub selector for admin only */}
          {user?.role === 'admin' && hubs.length > 0 && (
            <div className="w-full sm:w-48">
              <Label htmlFor="hub-select" className="text-xs text-muted-foreground">Hub</Label>
              <Select value={selectedHub} onValueChange={setSelectedHub}>
                <SelectTrigger id="hub-select" data-testid="dashboard-hub-select" className="h-9">
                  <SelectValue placeholder="Select Hub" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Hubs</SelectItem>
                  {hubs.map((hub) => (
                    <SelectItem key={hub.id} value={hub.id}>
                      {hub.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Date Filter */}
          <div className="w-full sm:w-40">
            <Label htmlFor="date-filter" className="text-xs text-muted-foreground">Date Range</Label>
            <Select value={dateFilter} onValueChange={handleDateFilterChange}>
              <SelectTrigger id="date-filter" data-testid="dashboard-date-filter" className="h-9">
                <SelectValue placeholder="Select Date" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Time</SelectItem>
                <SelectItem value="today">Today</SelectItem>
                <SelectItem value="custom">Custom Date</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Custom Date Picker */}
          {dateFilter === "custom" && (
            <div className="w-full sm:w-44">
              <Label htmlFor="custom-date" className="text-xs text-muted-foreground">Select Date</Label>
              <Input
                id="custom-date"
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                className="h-9"
                data-testid="dashboard-custom-date"
              />
            </div>
          )}

          {/* Refresh Button */}
          <Button 
            variant="outline" 
            size="sm" 
            onClick={fetchDashboardStats}
            className="h-9 gap-1"
            data-testid="dashboard-refresh"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            <span className="hidden sm:inline">Refresh</span>
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 lg:gap-6">
        {statsCards.map((stat, index) => (
          <Card key={index} className="hover:shadow-xl transition-all duration-300 border-2 hover:border-primary/30 bg-white" data-testid={`stat-card-${stat.title.toLowerCase().replace(/ /g, '-')}`}>
            <CardHeader className="flex flex-row items-center justify-between pb-1 sm:pb-2 p-3 sm:p-4 lg:p-6">
              <CardTitle className="text-[10px] sm:text-xs lg:text-sm font-semibold text-gray-600 uppercase tracking-wider truncate">
                {stat.title}
              </CardTitle>
              <div className={`p-1.5 sm:p-2 rounded-lg ${stat.bg} flex-shrink-0`}>
                <stat.icon className={`w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6 ${stat.color}`} />
              </div>
            </CardHeader>
            <CardContent className="p-3 sm:p-4 lg:p-6 pt-0">
              <div className={`text-lg sm:text-2xl lg:text-3xl font-bold font-heading ${stat.color}`}>
                {loading ? "..." : stat.value}
              </div>
              <p className="text-[10px] sm:text-xs text-muted-foreground mt-1">
                {stat.count !== "-" ? `${stat.count} entries` : "Net balance"}
              </p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        <Card className="hover:shadow-md transition-shadow duration-300">
          <CardHeader className="p-4 sm:p-6">
            <CardTitle className="text-lg sm:text-xl lg:text-2xl font-heading flex items-center gap-2">
              <Calendar className="w-5 h-5 text-muted-foreground" />
              Material Flow
            </CardTitle>
          </CardHeader>
          <CardContent className="p-2 sm:p-4 lg:p-6 pt-0">
            {loading ? (
              <div className="h-[220px] flex items-center justify-center text-muted-foreground">Loading...</div>
            ) : (
              <ResponsiveContainer width="100%" height={220}>
                <PieChart>
                  <Pie
                    data={chartData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, value }) => value > 0 ? `${name}: ${value.toFixed(0)}` : ''}
                    outerRadius={70}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {chartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.fill} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => `${value.toFixed(1)} kg`} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        <Card className="hover:shadow-md transition-shadow duration-300">
          <CardHeader className="p-4 sm:p-6">
            <CardTitle className="text-lg sm:text-xl lg:text-2xl font-heading">Operations Summary</CardTitle>
          </CardHeader>
          <CardContent className="p-2 sm:p-4 lg:p-6 pt-0">
            {loading ? (
              <div className="h-[220px] flex items-center justify-center text-muted-foreground">Loading...</div>
            ) : (
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip formatter={(value) => `${value.toFixed(1)} kg`} />
                  <Bar dataKey="value" radius={[8, 8, 0, 0]}>
                    {chartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.fill} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default DashboardPage;
